import { apiClient, ApiResponse } from './client';

interface ActiveBatchResponse {
  has_active_batch: boolean;
  batch_id?: string;
  // Add other batch properties as needed
}

export const getActiveBatch = async (sessionId: string): Promise<ApiResponse> => {
  // uncomment for testing
  // return {
  //   statusCode: 200,
  //   body: {
  //     message: 'Batch accepted manually',
  //     batch: {
  //       id: '123',
  //       earnings: 140,
  //       tip: 10,
  //       distance: 10,
  //       items: 10,
  //       units: 10,
  //       customers: 10,
  //       waypoint: {
  //         address: '123 Mai2 St',
  //         street: '123 Mai2 St',
  //         latitude: 100,
  //         longitude: 100
  //       }
  //     }

  //   }
  // }
  return apiClient.request({
    method: 'POST',
    url: '/instacart/active',
    data: {
      session_id: sessionId
    }
  });
};

// Type guard to check if response is ActiveBatchResponse
export const isActiveBatchResponse = (data: any): data is ActiveBatchResponse => {
  return typeof data === 'object' && 
         'has_active_batch' in data && 
         typeof data.has_active_batch === 'boolean';
};

export const pingAlive = async (sessionId: string): Promise<ApiResponse> => {
  return apiClient.request({
    method: 'POST',
    url: '/instacart/alive',
    data: {
      session_id: sessionId
    }
  });
};

export const getBatchFinderServiceSettings = async (sessionId: string): Promise<ApiResponse> => {
  return apiClient.request({
    method: 'POST',
    url: '/instacart/step/settings/get',
    data: {
      session_id: sessionId
    }
  });
};

export const startBatchFinderService = async (
  session_id: string,
  batchfinder_version: string
): Promise<ApiResponse> => {
  const config = {
    method: 'POST',
    url: '/instacart/step/start',
    headers: {
      'Content-Type': 'application/json',
    },
    data: { session_id, batchfinder_version },
  };

  try {
    const response = await apiClient.request(config);
    return response;
  } catch (error: any) {
    return {
      statusCode: error.response?.status || 500,
      body: error.response?.data || 'Unknown error occurred',
    };
  }
};

export const stopBatchFinderService = async (session_id: string): Promise<ApiResponse> => {
  const config = {
    method: 'POST',
    url: '/instacart/step/stop',
    headers: {
      'Content-Type': 'application/json',
    },
    data: { session_id },
  };

  try {
    const response = await apiClient.request(config);
    return response;
  } catch (error: any) {
    return {
      statusCode: error.response?.status || 500,
      body: error.response?.data || 'Unknown error occurred',
    };
  }
};

// New function to start a batch
export const startBatch = async (session_id: string): Promise<ApiResponse> => {
  return apiClient.request({
    method: 'POST',
    url: '/instacart/begin',
    data: { session_id }
  });
};

// Function to manually accept a batch
export const acceptBatchManually = async (
  session_id: string, 
  batch_id: string, 
  metadata: any
): Promise<ApiResponse> => {
  return apiClient.request({
    method: 'POST',
    url: '/instacart/accept',
    data: { session_id, batch_id, metadata }
  });
};

/**
 * Function to go online - required before starting Turbo service.
 * This is part of the additional verification flow for Premium service (turbo).
 */
export const goOnline = async (session_id: string): Promise<ApiResponse> => {
  return apiClient.request({
    method: 'POST',
    url: '/instacart/online',
    data: { session_id }
  });
};

/**
 * Function to verify user - may be required if goOnline returns "Verification required".
 * This is part of the additional verification flow for Premium service (turbo).
 */
export const verifyUser = async (session_id: string): Promise<ApiResponse> => {
  return apiClient.request({
    method: 'POST',
    url: '/instacart/enable',
    data: { session_id }
  });
};

export const getBatchHistory = async (session_id: string): Promise<ApiResponse> => {
  return apiClient.request({
    method: 'POST',
    url: '/batch/history', 
    data: { session_id }
  });
}; 

export const getQuickFilterCandidates = async (session_id: string): Promise<ApiResponse> => {
  return apiClient.request({
    method: 'POST',
    url: '/batch/quick-filter-candidates',
    data: { session_id }
  });
};

export const reviewQuickFilterCandidate = async (
  session_id: string,
  batch_id: string,
  action: 'accept' | 'reject'
): Promise<ApiResponse> => {
  return apiClient.request({
    method: 'POST',
    url: '/batch/review-quick-filter-candidate',
    data: { session_id, batch_id, action }
  });
};

export const getAccountEmail = async (session_id: string): Promise<ApiResponse> => {
  return apiClient.request({
    method: 'POST',
    url: '/email/get',
    data: { session_id }
  });
};

export const updateAccountEmail = async (
  session_id: string,
  email: string
): Promise<ApiResponse> => {
  return apiClient.request({
    method: 'POST',
    url: '/email/update',
    data: { session_id, email }
  });
};

/**
 * Reset Instacart credentials - desyncs and clears instacart_ahoy.
 * Used when a user's selfie verification has been rejected.
 */
export const resetInstacart = async (session_id: string): Promise<ApiResponse> => {
  return apiClient.request({
    method: 'POST',
    url: '/instacart/reset',
    data: { session_id }
  });
};

